import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-700',
  contacted: 'bg-yellow-100 text-yellow-700',
  quoted: 'bg-purple-100 text-purple-700',
  follow_up: 'bg-orange-100 text-orange-700',
  won: 'bg-green-100 text-green-700',
  lost: 'bg-red-100 text-red-700'
};

const STATUS_OPTIONS = ['new', 'contacted', 'quoted', 'follow_up', 'won', 'lost'];

export const CRMTab = ({ authHeaders }) => {
  const [view, setView] = useState('contacts'); // contacts | campaigns
  const [helpOpen, setHelpOpen] = useState(false);
  return (
    <div>
      <div className="mb-6" data-testid="crm-help-section">
        <button onClick={() => setHelpOpen(!helpOpen)} className="flex items-center gap-2 text-sm text-[#3a87fe] hover:text-[#2968d9] font-medium transition" data-testid="crm-help-toggle">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          {helpOpen ? 'Hide Instructions' : 'How do I use this?'}
          <svg className={`w-4 h-4 transition-transform ${helpOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
        </button>
        {helpOpen && (
          <div className="mt-3 bg-blue-50 border border-blue-100 rounded-xl p-5 text-sm text-gray-700 leading-relaxed space-y-3" data-testid="crm-help-content">
            <p><strong>CRM / Leads</strong> is where you manage every person who contacts you through your website.</p>
            <p><strong>Contacts & Leads tab:</strong></p>
            <ul className="list-disc ml-5 space-y-1">
              <li>Every time someone fills out the "Get Free Estimate" form or the Contact form, they appear here automatically.</li>
              <li>Each lead shows their <strong>name, email, phone, address, and what service they're interested in</strong>.</li>
              <li>Use the <strong>status dropdown</strong> to track where each lead is in your sales process:
                <ul className="list-disc ml-5 mt-1 space-y-0.5">
                  <li><strong>New</strong> - Just came in, hasn't been contacted yet.</li>
                  <li><strong>Contacted</strong> - You've reached out to them.</li>
                  <li><strong>Quoted</strong> - You've sent them a price estimate.</li>
                  <li><strong>Follow Up</strong> - Needs another call or email.</li>
                  <li><strong>Won</strong> - They hired you! Great job!</li>
                  <li><strong>Lost</strong> - They went with someone else or aren't interested.</li>
                </ul>
              </li>
              <li>Click on any lead to see <strong>full details</strong>, add <strong>notes</strong>, and <strong>send emails</strong> directly from here.</li>
              <li>Use the <strong>search bar</strong> to find leads by name, email, or phone number.</li>
              <li>Use the <strong>filter buttons</strong> to show only leads with a certain status.</li>
            </ul>
            <p><strong>Drip Campaigns tab:</strong></p>
            <ul className="list-disc ml-5 space-y-1">
              <li>Drip campaigns are automated email sequences sent to new leads over time.</li>
              <li>You can create campaigns with multiple email steps, each sent after a set number of days.</li>
              <li>This helps you follow up with potential customers automatically.</li>
            </ul>
            <p className="text-xs text-gray-500 mt-2">Tip: Check your "New" leads every day! Fast response times win more jobs.</p>
          </div>
        )}
      </div>
      <div className="flex gap-2 mb-6">
        <button onClick={() => setView('contacts')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition ${view === 'contacts' ? 'bg-[#5c0701] text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
          data-testid="crm-view-contacts">
          Contacts & Leads
        </button>
        <button onClick={() => setView('campaigns')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition ${view === 'campaigns' ? 'bg-[#5c0701] text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
          data-testid="crm-view-campaigns">
          Drip Campaigns
        </button>
      </div>
      {view === 'contacts' && <ContactsView authHeaders={authHeaders} />}
      {view === 'campaigns' && <CampaignsView authHeaders={authHeaders} />}
    </div>
  );
};

/* ═══════════════════════════════════════════
   CONTACTS VIEW
   ═══════════════════════════════════════════ */
const ContactsView = ({ authHeaders }) => {
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  const fetchContacts = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (statusFilter) params.append('status_filter', statusFilter);
    params.append('limit', '100');

    Promise.all([
      axios.get(`${API_URL}/api/crm/contacts?${params}`, authHeaders()),
      axios.get(`${API_URL}/api/crm/crm/stats`, authHeaders())
    ]).then(([contactsRes, statsRes]) => {
      setContacts(contactsRes.data.contacts || []);
      setTotal(contactsRes.data.total || 0);
      setStats(statsRes.data || {});
    }).catch(err => console.error(err))
    .finally(() => setLoading(false));
  }, [authHeaders, search, statusFilter]);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  return (
    <div>
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
        {[
          { label: 'Total', value: stats.total_contacts, color: 'bg-gray-50' },
          { label: 'New', value: stats.new, color: 'bg-blue-50' },
          { label: 'Contacted', value: stats.contacted, color: 'bg-yellow-50' },
          { label: 'Quoted', value: stats.quoted, color: 'bg-purple-50' },
          { label: 'Won', value: stats.won, color: 'bg-green-50' },
          { label: 'SMS Opted', value: stats.sms_opted_in, color: 'bg-orange-50' },
        ].map(s => (
          <div key={s.label} className={`${s.color} rounded-lg p-4 text-center`}>
            <div className="text-2xl font-bold">{s.value || 0}</div>
            <div className="text-xs text-gray-500">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Search & Filter */}
      <div className="flex flex-wrap gap-3 mb-4">
        <input
          type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search name, email, phone, city..."
          className="flex-1 min-w-[200px] px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#3a87fe] focus:border-transparent"
          data-testid="crm-search"
        />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm bg-white" data-testid="crm-status-filter">
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
        <button onClick={() => setShowAddForm(!showAddForm)}
          className="bg-[#3a87fe] text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-[#2968d9] transition"
          data-testid="crm-add-contact">
          {showAddForm ? 'Cancel' : '+ Add Contact'}
        </button>
      </div>

      {/* Add Contact Form */}
      {showAddForm && <AddContactForm authHeaders={authHeaders} onDone={() => { setShowAddForm(false); fetchContacts(); }} />}

      {/* Contact Detail Modal */}
      {selectedContact && (
        <ContactDetail
          contact={selectedContact}
          authHeaders={authHeaders}
          onClose={() => setSelectedContact(null)}
          onUpdate={fetchContacts}
        />
      )}

      {/* Contacts Table */}
      {loading ? (
        <div className="text-center py-12">Loading contacts...</div>
      ) : contacts.length === 0 ? (
        <div className="bg-white rounded-lg p-8 text-center text-gray-500">No contacts found</div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-3 font-semibold">Name</th>
                <th className="text-left p-3 font-semibold">Contact</th>
                <th className="text-left p-3 font-semibold">City</th>
                <th className="text-left p-3 font-semibold">Service</th>
                <th className="text-left p-3 font-semibold">Status</th>
                <th className="text-left p-3 font-semibold">SMS</th>
                <th className="text-left p-3 font-semibold">Source</th>
                <th className="text-left p-3 font-semibold">Date</th>
                <th className="text-left p-3 font-semibold w-20">Actions</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((c, idx) => (
                <tr key={c.lead_id || idx} className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedContact(c)} data-testid={`contact-row-${idx}`}>
                  <td className="p-3 font-medium">{c.name}</td>
                  <td className="p-3">
                    <div className="text-xs">{c.email}</div>
                    <div className="text-xs text-gray-500">{c.phone}</div>
                  </td>
                  <td className="p-3 text-xs">{c.city || '-'}</td>
                  <td className="p-3 text-xs">{c.service_interest || '-'}</td>
                  <td className="p-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[c.status] || 'bg-gray-100'}`}>
                      {(c.status || 'new').replace('_', ' ')}
                    </span>
                  </td>
                  <td className="p-3">
                    <span className={`w-5 h-5 inline-flex items-center justify-center rounded-full text-xs ${c.sms_opt_in ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                      {c.sms_opt_in ? '\u2713' : '\u2717'}
                    </span>
                  </td>
                  <td className="p-3 text-xs text-gray-500">{c.source || 'web'}</td>
                  <td className="p-3 text-xs text-gray-500">{c.created_at ? new Date(c.created_at).toLocaleDateString() : '-'}</td>
                  <td className="p-3" onClick={e => e.stopPropagation()}>
                    <button onClick={() => setSelectedContact(c)} className="text-[#3a87fe] text-xs hover:underline" data-testid={`view-contact-${idx}`}>
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="p-3 text-xs text-gray-500 border-t">Showing {contacts.length} of {total} contacts</div>
        </div>
      )}
    </div>
  );
};


/* ═══════════════════════════════════════════
   CONTACT DETAIL (Modal/Panel)
   ═══════════════════════════════════════════ */
const ContactDetail = ({ contact, authHeaders, onClose, onUpdate }) => {
  const [status, setStatus] = useState(contact.status || 'new');
  const [noteText, setNoteText] = useState('');
  const [emailForm, setEmailForm] = useState({ show: false, subject: '', body: '' });
  const [smsForm, setSmsForm] = useState({ show: false, message: '' });
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');

  const updateStatus = async (newStatus) => {
    try {
      await axios.patch(`${API_URL}/api/crm/contacts/${contact.lead_id}`, { status: newStatus }, authHeaders());
      setStatus(newStatus);
      onUpdate();
    } catch (err) { console.error(err); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      await axios.post(`${API_URL}/api/crm/contacts/${contact.lead_id}/notes`, { note: noteText }, authHeaders());
      setNoteText('');
      onUpdate();
      setMessage('Note added');
      setTimeout(() => setMessage(''), 2000);
    } catch (err) { console.error(err); }
  };

  const sendEmail = async () => {
    if (!emailForm.subject || !emailForm.body) return;
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/api/crm/contacts/send-email`, {
        lead_id: contact.lead_id, subject: emailForm.subject, body: emailForm.body
      }, authHeaders());
      setMessage(res.data.message || 'Email sent!');
      setEmailForm({ show: false, subject: '', body: '' });
      onUpdate();
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Failed to send email');
    } finally { setSending(false); setTimeout(() => setMessage(''), 4000); }
  };

  const sendSMS = async () => {
    if (!smsForm.message) return;
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/api/crm/contacts/send-sms`, {
        lead_id: contact.lead_id, message: smsForm.message
      }, authHeaders());
      setMessage(res.data.message || 'SMS sent/queued!');
      setSmsForm({ show: false, message: '' });
      onUpdate();
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Failed to send SMS');
    } finally { setSending(false); setTimeout(() => setMessage(''), 4000); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex justify-end" onClick={onClose} data-testid="contact-detail-modal">
      <div className="bg-white w-full max-w-xl h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="bg-[#5c0701] text-white p-5 flex justify-between items-start sticky top-0">
          <div>
            <h2 className="text-xl font-bold">{contact.name}</h2>
            <p className="text-sm opacity-80">{contact.email}</p>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white text-2xl leading-none" data-testid="close-detail">&times;</button>
        </div>

        <div className="p-5 space-y-6">
          {message && <div className="bg-blue-50 text-blue-700 border border-blue-200 px-4 py-2 rounded-lg text-sm">{message}</div>}

          {/* Contact Info */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><span className="text-gray-500 block text-xs">Phone</span><a href={`tel:${contact.phone}`} className="text-[#3a87fe]">{contact.phone}</a></div>
            <div><span className="text-gray-500 block text-xs">Email</span><a href={`mailto:${contact.email}`} className="text-[#3a87fe]">{contact.email}</a></div>
            <div><span className="text-gray-500 block text-xs">Address</span>{contact.address || '-'}</div>
            <div><span className="text-gray-500 block text-xs">City</span>{contact.city || '-'}</div>
            <div><span className="text-gray-500 block text-xs">Service Interest</span>{contact.service_interest || '-'}</div>
            <div><span className="text-gray-500 block text-xs">SMS Opt-In</span>
              <span className={contact.sms_opt_in ? 'text-green-600 font-medium' : 'text-gray-400'}>{contact.sms_opt_in ? 'Yes' : 'No'}</span>
            </div>
            <div><span className="text-gray-500 block text-xs">Source</span>{contact.source || 'website'}</div>
            <div><span className="text-gray-500 block text-xs">Date</span>{contact.created_at ? new Date(contact.created_at).toLocaleString() : '-'}</div>
          </div>

          {/* Original Message */}
          {contact.message && (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 mb-1">Original Message</h4>
              <div className="bg-gray-50 p-3 rounded-lg text-sm text-gray-700">{contact.message}</div>
            </div>
          )}

          {/* Status */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 mb-2">Lead Status</h4>
            <div className="flex flex-wrap gap-2">
              {STATUS_OPTIONS.map(s => (
                <button key={s} onClick={() => updateStatus(s)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${status === s ? STATUS_COLORS[s] + ' ring-2 ring-offset-1 ring-gray-300' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                  data-testid={`status-btn-${s}`}>
                  {s.replace('_', ' ')}
                </button>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex gap-2">
            <button onClick={() => { setEmailForm({ ...emailForm, show: !emailForm.show }); setSmsForm({ ...smsForm, show: false }); }}
              className="flex-1 bg-[#3a87fe] text-white py-2.5 rounded-lg text-sm font-medium hover:bg-[#2968d9] transition"
              data-testid="send-email-btn">
              Send Email
            </button>
            <button onClick={() => { setSmsForm({ ...smsForm, show: !smsForm.show }); setEmailForm({ ...emailForm, show: false }); }}
              className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition ${contact.sms_opt_in ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
              disabled={!contact.sms_opt_in}
              data-testid="send-sms-btn">
              {contact.sms_opt_in ? 'Send SMS' : 'SMS Not Opted In'}
            </button>
          </div>

          {/* Email Form */}
          {emailForm.show && (
            <div className="border border-[#3a87fe] rounded-lg p-4 space-y-3">
              <h4 className="font-semibold text-sm text-[#3a87fe]">Send Email to {contact.name}</h4>
              <input value={emailForm.subject} onChange={e => setEmailForm({ ...emailForm, subject: e.target.value })}
                placeholder="Subject line" className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="email-subject" />
              <textarea value={emailForm.body} onChange={e => setEmailForm({ ...emailForm, body: e.target.value })}
                placeholder="Email body..." rows="5" className="w-full px-3 py-2 border rounded-lg text-sm resize-none" data-testid="email-body" />
              <button onClick={sendEmail} disabled={sending || !emailForm.subject || !emailForm.body}
                className="bg-[#3a87fe] text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-[#2968d9] transition disabled:opacity-50"
                data-testid="email-send">
                {sending ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          )}

          {/* SMS Form */}
          {smsForm.show && (
            <div className="border border-green-500 rounded-lg p-4 space-y-3">
              <h4 className="font-semibold text-sm text-green-600">Send SMS to {contact.phone}</h4>
              <p className="text-xs text-gray-500">SMS integration pending activation. Messages will be queued and sent once active.</p>
              <textarea value={smsForm.message} onChange={e => setSmsForm({ ...smsForm, message: e.target.value })}
                placeholder="SMS message (max 1600 chars)..." rows="3" maxLength={1600}
                className="w-full px-3 py-2 border rounded-lg text-sm resize-none" data-testid="sms-body" />
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-400">{smsForm.message.length}/1600</span>
                <button onClick={sendSMS} disabled={sending || !smsForm.message}
                  className="bg-green-600 text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition disabled:opacity-50"
                  data-testid="sms-send">
                  {sending ? 'Sending...' : 'Send SMS'}
                </button>
              </div>
            </div>
          )}

          {/* Add Note */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 mb-2">Add Note</h4>
            <div className="flex gap-2">
              <textarea value={noteText} onChange={e => setNoteText(e.target.value)}
                placeholder="Add a note about this contact..." rows="2"
                className="flex-1 px-3 py-2 border rounded-lg text-sm resize-none" data-testid="note-input" />
              <button onClick={addNote} disabled={!noteText.trim()}
                className="bg-gray-700 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 transition disabled:opacity-50 self-end"
                data-testid="note-submit">
                Add
              </button>
            </div>
          </div>

          {/* Activity Notes */}
          {contact.notes && contact.notes.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 mb-2">Activity Log ({contact.notes.length})</h4>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {[...contact.notes].reverse().map((n, i) => (
                  <div key={i} className="bg-gray-50 p-3 rounded-lg text-sm">
                    <p className="text-gray-700">{n.note}</p>
                    <p className="text-xs text-gray-400 mt-1">{n.author} &middot; {n.timestamp ? new Date(n.timestamp).toLocaleString() : ''}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          {contact.tags && contact.tags.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 mb-2">Tags</h4>
              <div className="flex flex-wrap gap-2">
                {contact.tags.map((tag, i) => (
                  <span key={i} className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-xs">{tag}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


/* ═══════════════════════════════════════════
   ADD CONTACT FORM
   ═══════════════════════════════════════════ */
const AddContactForm = ({ authHeaders, onDone }) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', address: '', city: '', service_interest: '', notes: '', sms_opt_in: false, source: 'manual' });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!form.name) return;
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/crm/contacts/manual`, form, authHeaders());
      onDone();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to add contact');
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6 border border-[#3a87fe]" data-testid="add-contact-form">
      <h3 className="font-bold mb-4 text-[#5c0701]">Add New Contact</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Name *" className="px-3 py-2 border rounded-lg text-sm" data-testid="add-name" />
        <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="Email" type="email" className="px-3 py-2 border rounded-lg text-sm" data-testid="add-email" />
        <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="Phone" className="px-3 py-2 border rounded-lg text-sm" data-testid="add-phone" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Address" className="px-3 py-2 border rounded-lg text-sm" />
        <input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} placeholder="City" className="px-3 py-2 border rounded-lg text-sm" />
        <select value={form.service_interest} onChange={e => setForm({ ...form, service_interest: e.target.value })} className="px-3 py-2 border rounded-lg text-sm bg-white">
          <option value="">Service Interest</option>
          <option value="Kitchen Remodeling">Kitchen Remodeling</option>
          <option value="Bathroom Remodeling">Bathroom Remodeling</option>
          <option value="Deck Building">Deck Building</option>
          <option value="Basement Finishing">Basement Finishing</option>
          <option value="Vinyl Siding">Vinyl Siding</option>
          <option value="Window Replacement">Window Replacement</option>
          <option value="Custom Furniture & Aquariums">Custom Furniture & Aquariums</option>
          <option value="Home Maintenance">Home Maintenance</option>
        </select>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <select value={form.source} onChange={e => setForm({ ...form, source: e.target.value })} className="px-3 py-2 border rounded-lg text-sm bg-white">
          <option value="manual">Manual Entry</option>
          <option value="phone_call">Phone Call</option>
          <option value="referral">Referral</option>
        </select>
        <label className="flex items-center gap-2 px-3 py-2">
          <input type="checkbox" checked={form.sms_opt_in} onChange={e => setForm({ ...form, sms_opt_in: e.target.checked })} className="w-4 h-4" />
          <span className="text-sm text-gray-700">SMS Opt-In (verbal/written consent obtained)</span>
        </label>
      </div>
      <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Notes about this contact..." rows="2" className="w-full px-3 py-2 border rounded-lg text-sm resize-none mb-3" />
      <button onClick={handleSubmit} disabled={saving || !form.name}
        className="bg-[#5c0701] text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-[#7a0901] transition disabled:opacity-50"
        data-testid="add-contact-submit">
        {saving ? 'Adding...' : 'Add Contact'}
      </button>
    </div>
  );
};


/* ═══════════════════════════════════════════
   CAMPAIGNS VIEW
   ═══════════════════════════════════════════ */
const CampaignsView = ({ authHeaders }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const fetchCampaigns = useCallback(() => {
    setLoading(true);
    axios.get(`${API_URL}/api/crm/campaigns`, authHeaders())
      .then(res => setCampaigns(res.data.campaigns || []))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [authHeaders]);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  const toggleActive = async (campaign) => {
    try {
      await axios.patch(`${API_URL}/api/crm/campaigns/${campaign.campaign_id}`, { is_active: !campaign.is_active }, authHeaders());
      fetchCampaigns();
    } catch (err) { console.error(err); }
  };

  const deleteCampaign = async (id) => {
    if (!window.confirm('Delete this campaign?')) return;
    try {
      await axios.delete(`${API_URL}/api/crm/campaigns/${id}`, authHeaders());
      fetchCampaigns();
    } catch (err) { console.error(err); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">Drip Campaigns</h2>
          <p className="text-sm text-gray-500">Create automated email/SMS sequences for lead nurturing</p>
        </div>
        <button onClick={() => setShowCreate(!showCreate)}
          className="bg-[#3a87fe] text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-[#2968d9] transition"
          data-testid="create-campaign-btn">
          {showCreate ? 'Cancel' : '+ New Campaign'}
        </button>
      </div>

      {showCreate && <CreateCampaignForm authHeaders={authHeaders} onDone={() => { setShowCreate(false); fetchCampaigns(); }} />}

      {loading ? (
        <div className="text-center py-12">Loading campaigns...</div>
      ) : campaigns.length === 0 ? (
        <div className="bg-white rounded-lg p-8 text-center text-gray-500">
          <p className="text-lg font-medium mb-2">No drip campaigns yet</p>
          <p className="text-sm">Create your first campaign to automatically nurture leads with timed email and SMS sequences.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {campaigns.map(c => (
            <div key={c.campaign_id} className="bg-white rounded-lg shadow p-5" data-testid={`campaign-${c.campaign_id}`}>
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-bold text-lg">{c.name}</h3>
                  <p className="text-sm text-gray-500">{c.description}</p>
                  <div className="flex gap-3 mt-2 text-xs text-gray-400">
                    <span>{c.steps?.length || 0} steps</span>
                    <span>{c.enrolled_count || 0} enrolled</span>
                    {c.target_services?.length > 0 && <span>Target: {c.target_services.join(', ')}</span>}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => toggleActive(c)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${c.is_active ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                    {c.is_active ? 'Active' : 'Paused'}
                  </button>
                  <button onClick={() => deleteCampaign(c.campaign_id)}
                    className="bg-red-50 text-red-600 px-3 py-1.5 rounded-lg text-xs hover:bg-red-100 transition">
                    Delete
                  </button>
                </div>
              </div>

              {/* Steps */}
              {c.steps && c.steps.length > 0 && (
                <div className="mt-4 border-t pt-4">
                  <h4 className="text-xs font-semibold text-gray-500 mb-2">Campaign Steps</h4>
                  <div className="space-y-2">
                    {c.steps.map((step, idx) => (
                      <div key={idx} className="flex items-center gap-3 text-sm">
                        <div className="w-6 h-6 rounded-full bg-[#5c0701] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">{step.step_number}</div>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${step.channel === 'email' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                          {step.channel}
                        </span>
                        <span className="text-gray-500 text-xs">Day {step.delay_days}</span>
                        <span className="text-gray-700 text-xs flex-1 truncate">{step.subject || step.body.substring(0, 60)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


/* ═══════════════════════════════════════════
   CREATE CAMPAIGN FORM
   ═══════════════════════════════════════════ */
const CreateCampaignForm = ({ authHeaders, onDone }) => {
  const [form, setForm] = useState({ name: '', description: '', target_services: [] });
  const [steps, setSteps] = useState([{ delay_days: 0, channel: 'email', subject: '', body: '' }]);
  const [saving, setSaving] = useState(false);

  const addStep = () => {
    const lastDay = steps.length > 0 ? steps[steps.length - 1].delay_days : 0;
    setSteps([...steps, { delay_days: lastDay + 3, channel: 'email', subject: '', body: '' }]);
  };

  const removeStep = (idx) => {
    setSteps(steps.filter((_, i) => i !== idx));
  };

  const updateStep = (idx, field, value) => {
    const updated = [...steps];
    updated[idx] = { ...updated[idx], [field]: value };
    setSteps(updated);
  };

  const handleSubmit = async () => {
    if (!form.name || steps.length === 0) return;
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/crm/campaigns`, { ...form, steps }, authHeaders());
      onDone();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create campaign');
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6 border border-[#3a87fe]" data-testid="create-campaign-form">
      <h3 className="font-bold mb-4 text-[#5c0701]">Create Drip Campaign</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
          placeholder="Campaign name *" className="px-3 py-2 border rounded-lg text-sm" data-testid="campaign-name" />
        <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
          placeholder="Description" className="px-3 py-2 border rounded-lg text-sm" />
      </div>

      <h4 className="font-semibold text-sm mb-3">Campaign Steps</h4>
      <div className="space-y-4 mb-4">
        {steps.map((step, idx) => (
          <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <div className="flex justify-between items-center mb-3">
              <span className="font-medium text-sm">Step {idx + 1}</span>
              {steps.length > 1 && (
                <button onClick={() => removeStep(idx)} className="text-red-500 text-xs hover:underline">Remove</button>
              )}
            </div>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Delay (days)</label>
                <input type="number" min="0" value={step.delay_days} onChange={e => updateStep(idx, 'delay_days', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border rounded text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Channel</label>
                <select value={step.channel} onChange={e => updateStep(idx, 'channel', e.target.value)}
                  className="w-full px-3 py-2 border rounded text-sm bg-white">
                  <option value="email">Email</option>
                  <option value="sms">SMS</option>
                </select>
              </div>
              {step.channel === 'email' && (
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Subject</label>
                  <input value={step.subject || ''} onChange={e => updateStep(idx, 'subject', e.target.value)}
                    placeholder="Email subject" className="w-full px-3 py-2 border rounded text-sm" />
                </div>
              )}
            </div>
            <label className="text-xs text-gray-500 block mb-1">Message Body</label>
            <textarea value={step.body} onChange={e => updateStep(idx, 'body', e.target.value)}
              placeholder={step.channel === 'sms' ? 'SMS message (max 1600 chars)' : 'Email body content'}
              rows="3" className="w-full px-3 py-2 border rounded text-sm resize-none" />
          </div>
        ))}
      </div>

      <div className="flex gap-3">
        <button onClick={addStep} className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-300 transition">
          + Add Step
        </button>
        <button onClick={handleSubmit} disabled={saving || !form.name}
          className="bg-[#5c0701] text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-[#7a0901] transition disabled:opacity-50"
          data-testid="campaign-submit">
          {saving ? 'Creating...' : 'Create Campaign'}
        </button>
      </div>
    </div>
  );
};

export default CRMTab;
